/**
 * The task-clock editor (wave b12 frontend item 5; wave COM item 1), a configuration
 * panel: one row per task kind (hours to due, the reminder, the escalation and its
 * rank) and the tenant's witness grace, through GET and PATCH
 * /api/configuration/task-clocks.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Each save names ONE kind and every field of its clock (the server writes each named
 * field), or the witness grace alone. The refusals are the server's, said inside the
 * dialog in its words: 409 configuration.unchanged (nothing named differs), 422
 * configuration.kind_repeated and configuration.nothing_named, and a field-named 422
 * marks its control (`clocks.0.<field>`, `witness_grace_hours`).
 */

import { useMemo, useState } from "react";

import { ApiError } from "../api/client";
import { readTaskClocks, setTaskClocks, type TaskClock } from "../api/configuration";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { EmptyState, SkeletonRows } from "./parts";
import { taskTitle } from "./TasksPage";
import { useQuery } from "./useQuery";

export function TaskClocksPanel(): React.JSX.Element {
  const token = useToken();
  const copy = strings.configuration;
  const [stamp, setStamp] = useState(0);
  const [editing, setEditing] = useState<TaskClock | null>(null);
  const [grace, setGrace] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const clocks = useQuery((signal) => readTaskClocks(token, signal), [token, stamp]);
  const columns = useMemo(() => clockColumns(setEditing), []);

  return (
    <div className="q-ui flex min-w-0 flex-col gap-3" data-testid="configuration-task-clocks">
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      {clocks.kind === "loading" && <SkeletonRows />}
      {(clocks.kind === "failed" || clocks.kind === "absence") && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {clocks.kind === "ready" && (
        <>
          <div className="q-subform" data-testid="witness-grace">
            <p className="q-field__label">{copy.graceHeading}</p>
            <p data-testid="witness-grace-hours">{copy.graceBody(clocks.data.witnessGraceHours)}</p>
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="witness-grace-edit"
              onClick={() => {
                setGrace(clocks.data.witnessGraceHours);
              }}
            >
              {copy.graceEdit}
            </button>
          </div>
          {clocks.data.items.length === 0 ? (
            <EmptyState title={copy.taskClocksHeading} body={copy.taskClocksEmpty} />
          ) : (
            <DataTable
              data={clocks.data.items}
              columns={columns}
              caption={copy.taskClocksHeading}
              testId="task-clocks"
              getRowId={(row) => row.id}
              getRowTestId={() => "task-clock-row"}
              pageSize={50}
            />
          )}
        </>
      )}
      {editing !== null && (
        <ClockDialog
          clock={editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={() => {
            setToast(copy.clockSaved(taskTitle(editing.taskKind)));
            setEditing(null);
            setStamp((one) => one + 1);
          }}
        />
      )}
      {grace !== null && (
        <GraceDialog
          hours={grace}
          onClose={() => {
            setGrace(null);
          }}
          onSaved={(hours) => {
            setToast(copy.graceSaved(hours));
            setGrace(null);
            setStamp((one) => one + 1);
          }}
        />
      )}
    </div>
  );
}

function percent(fraction: number): number {
  return Math.round(fraction * 100);
}

function clockColumns(onEdit: (row: TaskClock) => void): RegisterColumn<TaskClock>[] {
  const copy = strings.configuration;
  const due = (row: TaskClock): string =>
    row.hoursToDue === null ? copy.noDueTime : copy.hours(row.hoursToDue);
  const timed = (row: TaskClock, text: string): string => (row.hoursToDue === null ? "" : text);
  return [
    {
      id: "kind",
      label: copy.clockKindColumn,
      text: (row) => taskTitle(row.taskKind),
      className: "min-w-[16rem]",
    },
    {
      id: "due",
      label: copy.clockDueColumn,
      text: due,
      sortValue: (row) => row.hoursToDue ?? Number.MAX_SAFE_INTEGER,
      render: (row) => <span data-testid="task-clock-due">{due(row)}</span>,
    },
    {
      id: "reminder",
      label: copy.clockReminderColumn,
      text: (row) => timed(row, copy.reminderAt(percent(row.reminderFraction))),
      sortValue: (row) => row.reminderFraction,
    },
    {
      id: "escalation",
      label: copy.clockEscalationColumn,
      text: (row) => timed(row, copy.escalationAfter(row.escalationHoursAfterDue)),
      sortValue: (row) => row.escalationHoursAfterDue,
    },
    {
      id: "to",
      label: copy.clockEscalateToColumn,
      text: (row) =>
        timed(
          row,
          row.escalateToRank === null
            ? copy.escalateToLead
            : copy.escalateToRank(row.escalateToRank),
        ),
      filter: true,
    },
    {
      id: "act",
      label: strings.nav.actions,
      text: () => copy.clockEdit,
      render: (row) => (
        <button
          type="button"
          className="q-button q-button--quiet"
          data-testid="task-clock-edit"
          onClick={() => {
            onEdit(row);
          }}
        >
          {copy.clockEdit}
        </button>
      ),
    },
  ];
}

/** A whole number inside [min, max], or null when the text is empty; undefined if bad. */
function whole(text: string, min: number, max: number): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  if (!/^\d+$/u.test(trimmed)) {
    return undefined;
  }
  const value = Number(trimmed);
  return value >= min && value <= max ? value : undefined;
}

function ClockDialog({
  clock,
  onClose,
  onSaved,
}: {
  readonly clock: TaskClock;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.configuration;
  const [due, setDue] = useState(clock.hoursToDue === null ? "" : String(clock.hoursToDue));
  const [reminder, setReminder] = useState(String(percent(clock.reminderFraction)));
  const [escalation, setEscalation] = useState(String(clock.escalationHoursAfterDue));
  const [rank, setRank] = useState(
    clock.escalateToRank === null ? "" : String(clock.escalateToRank),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const hoursToDue = whole(due, 1, 8760);
  const reminderPercent = whole(reminder, 1, 99);
  const escalationHours = whole(escalation, 0, 8760);
  const escalateTo = whole(rank, 1, 20);
  const valid =
    hoursToDue !== undefined &&
    typeof reminderPercent === "number" &&
    typeof escalationHours === "number" &&
    escalateTo !== undefined;

  async function send(): Promise<void> {
    if (!valid) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setTaskClocks(token, {
        clocks: [
          {
            task_kind: clock.taskKind,
            hours_to_due: hoursToDue,
            reminder_fraction: reminderPercent / 100,
            escalation_hours_after_due: escalationHours,
            escalate_to_rank: escalateTo,
          },
        ],
      });
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={copy.clockHeading(taskTitle(clock.taskKind))}
      onClose={onClose}
      error={error}
    >
      <p className="q-shell__lede">{copy.clockLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="clock-hours-to-due">
          {copy.clockDueLabel}
        </label>
        <p className="q-note" id="clock-hours-to-due-hint">
          {copy.clockDueHint}
        </p>
        <ServerField field="clocks.0.hours_to_due">
          <input
            id="clock-hours-to-due"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="1"
            max="8760"
            step="1"
            data-testid="clock-hours-to-due"
            aria-describedby="clock-hours-to-due-hint"
            value={due}
            onChange={(event) => {
              setDue(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="clock-reminder">
          {copy.clockReminderLabel}
        </label>
        <p className="q-note" id="clock-reminder-hint">
          {copy.clockReminderHint}
        </p>
        <ServerField field="clocks.0.reminder_fraction">
          <input
            id="clock-reminder"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="1"
            max="99"
            step="1"
            data-testid="clock-reminder"
            aria-describedby="clock-reminder-hint"
            value={reminder}
            onChange={(event) => {
              setReminder(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="clock-escalation">
          {copy.clockEscalationLabel}
        </label>
        <p className="q-note" id="clock-escalation-hint">
          {copy.clockEscalationHint}
        </p>
        <ServerField field="clocks.0.escalation_hours_after_due">
          <input
            id="clock-escalation"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="0"
            max="8760"
            step="1"
            data-testid="clock-escalation"
            aria-describedby="clock-escalation-hint"
            value={escalation}
            onChange={(event) => {
              setEscalation(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="clock-rank">
          {copy.clockRankLabel}
        </label>
        <p className="q-note" id="clock-rank-hint">
          {copy.clockRankHint}
        </p>
        <ServerField field="clocks.0.escalate_to_rank">
          <input
            id="clock-rank"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="1"
            max="20"
            step="1"
            data-testid="clock-rank"
            aria-describedby="clock-rank-hint"
            value={rank}
            onChange={(event) => {
              setRank(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {!valid && (
        <p className="q-note" data-testid="clock-whole">
          {copy.clockWhole}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="clock-save"
          disabled={busy || !valid}
          onClick={() => {
            void send();
          }}
        >
          {copy.clockSave}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}

function GraceDialog({
  hours,
  onClose,
  onSaved,
}: {
  readonly hours: number;
  readonly onClose: () => void;
  readonly onSaved: (hours: number) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.configuration;
  const [value, setValue] = useState(String(hours));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const grace = whole(value, 0, 720);

  async function send(): Promise<void> {
    if (typeof grace !== "number") {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await setTaskClocks(token, { witness_grace_hours: grace });
      onSaved(saved.witnessGraceHours);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={copy.graceDialogHeading} onClose={onClose} error={error}>
      <div className="q-field">
        <label className="q-field__label" htmlFor="witness-grace-input">
          {copy.graceLabel}
        </label>
        <p className="q-note" id="witness-grace-hint">
          {copy.graceHint}
        </p>
        <ServerField field="witness_grace_hours">
          <input
            id="witness-grace-input"
            className="q-input"
            type="number"
            inputMode="numeric"
            min="0"
            max="720"
            step="1"
            data-testid="witness-grace-input"
            aria-describedby="witness-grace-hint"
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        </ServerField>
      </div>
      {typeof grace !== "number" && (
        <p className="q-note" data-testid="witness-grace-whole">
          {copy.clockWhole}
        </p>
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="witness-grace-save"
          disabled={busy || typeof grace !== "number"}
          onClick={() => {
            void send();
          }}
        >
          {copy.graceSave}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
